import { DestroyRef, inject, makeStateKey, TransferState } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, Observable, skip, take, tap } from 'rxjs';
import { changei18n } from '@infragistics/igniteui-angular';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { ICON_REGISTER_LIST } from '@zhik/core/constants/icons-config';
import { IconService } from '@zhik/core/services/icon';
import { LocaleService } from '@zhik/core/services/locale';
import { PageMetadataService } from '@zhik/core/services/page-metadata';
import { PlatformDataService } from '@zhik/core/services/platform-data';
import { CookieConsentStore } from '@zhik/core/store/cookie-consent';
import { DskHomeUserStore } from '@zhik/core/store/dskhome-user';
import { MortgageApplicationStore } from '@zhik/core/store/mortgage-journey/mortgage-application';
import { CURRENT_ENVIRONMENT_TOKEN } from '@zhik/core/providers/environment-providers';
import { EnvironmentTypes } from '@zhik/core/models/environments';
import { UserInfoResponse } from '@zhik/core/models/dskhome-user';

const SESSION_EXISTS_KEY = makeStateKey<boolean>('session_exists');

export function appInitializerFactory(): Observable<UserInfoResponse> {
  const destroyRef = inject(DestroyRef);
  const platformDataService = inject(PlatformDataService);
  const dskHomeUserStore = inject(DskHomeUserStore);
  const localeService = inject(LocaleService);
  const pageMetadataService = inject(PageMetadataService);
  const iconService = inject(IconService);
  const cookieConsentStore = inject(CookieConsentStore);
  const ssrCookieService = inject(SsrCookieService);
  const transferState = inject(TransferState);
  const mortgageApplicationStore = inject(MortgageApplicationStore);
  const currentEnvironment = inject(CURRENT_ENVIRONMENT_TOKEN);

  iconService.registerIcons(ICON_REGISTER_LIST);
  changei18n(localeService.igxResourceStrings);

  updateSeo(platformDataService, pageMetadataService);

  setupCookieConsentStateSubs(destroyRef, cookieConsentStore, platformDataService);
  setupUserInfoStateSubs(destroyRef, dskHomeUserStore, mortgageApplicationStore, platformDataService);
  return getUserInfo(platformDataService, dskHomeUserStore, ssrCookieService, transferState, currentEnvironment);
}

function getUserInfo(
  platformDataService: PlatformDataService,
  dskHomeUserStore: DskHomeUserStore,
  ssrCookieService: SsrCookieService,
  transferState: TransferState,
  currentEnvironment: EnvironmentTypes
): Observable<UserInfoResponse> {
  let hasSessionCookies = false;

  if (platformDataService.isServer) {
    // 1. Server-side: Check if both cookies necessary for login are present
    hasSessionCookies = !!ssrCookieService.get('SESSION') && !!ssrCookieService.get('SESSION_EXPIRE');
    // 2. Save the result so the browser can see it
    transferState.set(SESSION_EXISTS_KEY, hasSessionCookies);
  } else {
    // 3. Browser-side: Retrieve the result saved by the server
    hasSessionCookies = transferState.get(SESSION_EXISTS_KEY, false);
    // 4. Remove only in the browser after it has been read
    transferState.remove(SESSION_EXISTS_KEY);
  }

  /**
   * WHY THIS BLOCK EXISTS:
   * On the live site, the server can see your login cookies (thus try to login
   * only when both cookies are present). But on 
   * 'localhost', the server and browser don't always share cookies properly.
   * 
   * PROBLEM: Without this, `hasSession` would be false on your local machine, 
   * and you'd be logged out every time you save a file while developing.
   * 
   * SOLUTION: In `development` mode, we skip the cookie check and just 
   * try to get the user info anyway. It might fail on the server (because cookies are missing),
   * but it will succeed in your browser as soon as the page loads.
   */
  if (hasSessionCookies || currentEnvironment === EnvironmentTypes.LOCAL) {
    return dskHomeUserStore.getInitialUserInfoOnServer().pipe(
      catchError((err) => {
        if (platformDataService.isServer && currentEnvironment !== EnvironmentTypes.LOCAL) {
          console.error('GET user-info failed even though SESSION and SESSION_EXPIRE are present!', err);
        }

        return EMPTY;
      })
    );
  }

  return EMPTY;
}

function updateSeo(platformDataService: PlatformDataService, pageMetadataService: PageMetadataService): void {
  if (platformDataService.isServer) {
    pageMetadataService.updateOgLocaleTag();
    pageMetadataService.setPreconnectLinks();
  }
}

function setupCookieConsentStateSubs(
  destroyRef: DestroyRef,
  cookieConsentStore: CookieConsentStore,
  platformDataService: PlatformDataService
): void {
  if (platformDataService.isBrowser) {
    toObservable(cookieConsentStore.cookieConsentState).pipe(
      skip(1), // skip initial value
      tap(() => {
        platformDataService.window?.location.reload();
      }),
      takeUntilDestroyed(destroyRef)
    ).subscribe();
  }
}

function setupUserInfoStateSubs(
  destroyRef: DestroyRef,
  dskHomeUserStore: DskHomeUserStore,
  mortgageApplicationStore: MortgageApplicationStore,
  platformDataService: PlatformDataService
): void {
  if (platformDataService.isBrowser) {
    toObservable(dskHomeUserStore.userId).pipe(
      skip(1), // skip initial value
      tap((userId) => {
        if (!userId) {
          mortgageApplicationStore.resetState();
        } else {
          mortgageApplicationStore.getState().pipe(take(1)).subscribe();
        }
      }),
      takeUntilDestroyed(destroyRef)
    ).subscribe();
  }
}
